/**
 * Quiz — widget de múltipla escolha ao vivo.
 *
 * Ciclo (ver docs/module_lab/ADAPTER_CONTRACT.md §Quiz):
 *   idle → (master abre) → open → (alunos respondem) → (master fecha) → closed
 *
 * Master: vê distribuição + contador em tempo real; botões abrir/fechar/reset.
 * Player: seleciona opção; resposta bloqueada após clique; vê contagem
 * agregada (sem correctIndex) até master fechar.
 */

import { useMemo, useState } from 'react'

import { useAdapter } from '../adapter'
import { useQuiz } from '../hooks/useQuiz'
import type { QuizSlide as QuizSlideModel } from '../types/manifest'

interface Props {
  slide: QuizSlideModel
}

export function Quiz({ slide }: Props) {
  const adapter = useAdapter()
  const isMaster = adapter.role === 'master'
  const state = useQuiz(slide.questionId)

  const status = state?.status ?? 'idle'
  const distribution = state?.distribution ?? []
  const total = state?.responses ?? 0
  const correctIndex = state?.correctIndex ?? null

  const [mySelection, setMySelection] = useState<number | null>(null)

  // reset selection ao mudar de rodada
  useMemo(() => {
    if (status === 'idle') setMySelection(null)
  }, [status])

  function handleSelect(i: number) {
    if (status !== 'open' || isMaster || mySelection !== null) return
    setMySelection(i)
    adapter.submitAnswer(slide.questionId, i)
  }

  function pct(i: number): number {
    if (total === 0) return 0
    return Math.round(((distribution[i] ?? 0) / total) * 100)
  }

  return (
    <article style={{ fontFamily: 'var(--font-lab-sans)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Pill status={status} />
        {status === 'open' && (
          <span
            style={{
              fontSize: 'var(--text-lab-sm)',
              color: 'var(--color-lab-ink-3)',
              fontFamily: 'var(--font-lab-mono)',
            }}
          >
            {total} resposta{total === 1 ? '' : 's'}
          </span>
        )}
      </header>

      <h1 style={{ fontSize: 'var(--text-lab-xl)', lineHeight: 1.25, marginTop: 0 }}>
        {slide.stem}
      </h1>

      <div
        role="list"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lab-3)', marginTop: 'var(--spacing-lab-4)' }}
      >
        {slide.options.map((label, i) => {
          const letter = String.fromCharCode(65 + i)
          const isSelected = mySelection === i
          const isCorrect = status === 'closed' && correctIndex === i
          const isWrong = status === 'closed' && mySelection === i && correctIndex !== i
          const disabled = status !== 'open' || isMaster || mySelection !== null

          return (
            <button
              key={i}
              type="button"
              role="listitem"
              onClick={() => handleSelect(i)}
              disabled={disabled}
              style={{
                minHeight: 'var(--lab-tap-min)',
                padding: '12px 16px',
                display: 'grid',
                gridTemplateColumns: '32px 1fr auto',
                alignItems: 'center',
                gap: 12,
                background: optionBg(isSelected, isCorrect, isWrong),
                color: 'var(--color-lab-ink)',
                border: `2px solid ${optionBorder(isSelected, isCorrect, isWrong)}`,
                borderRadius: 'var(--radius-lab)',
                fontFamily: 'inherit',
                fontSize: 'var(--text-lab-base)',
                textAlign: 'left',
                cursor: disabled ? 'default' : 'pointer',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-lab-mono)',
                  fontWeight: 700,
                  color: 'var(--color-lab-ink-3)',
                }}
              >
                {letter}
              </span>
              <span>{label}</span>
              {(status === 'open' || status === 'closed') && (
                <span
                  style={{
                    fontFamily: 'var(--font-lab-mono)',
                    fontSize: 'var(--text-lab-sm)',
                    color: 'var(--color-lab-ink-3)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {pct(i)}%
                </span>
              )}
              {(status === 'open' || status === 'closed') && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    height: 3,
                    width: `${pct(i)}%`,
                    background: barColor(isCorrect, isWrong),
                    transition: 'width 250ms',
                  }}
                />
              )}
            </button>
          )
        })}
      </div>

      {isMaster && (
        <div style={{ display: 'flex', gap: 8, marginTop: 'var(--spacing-lab-4)' }}>
          {status === 'idle' && (
            <button
              type="button"
              style={btnPrimary}
              onClick={() =>
                adapter.openQuiz({
                  questionId: slide.questionId,
                  options: slide.options,
                  correctIndex: slide.correctIndex,
                })
              }
            >
              Abrir quiz
            </button>
          )}
          {status === 'open' && (
            <button
              type="button"
              style={btnPrimary}
              onClick={() => adapter.closeQuiz(slide.questionId)}
            >
              Fechar &amp; revelar
            </button>
          )}
          {status === 'closed' && (
            <button
              type="button"
              style={btnSecondary}
              onClick={() => adapter.resetQuiz(slide.questionId)}
            >
              Nova rodada
            </button>
          )}
        </div>
      )}

      {!isMaster && mySelection !== null && status === 'open' && (
        <p style={{ marginTop: 'var(--spacing-lab-4)', color: 'var(--color-lab-ink-3)' }}>
          Resposta enviada. Aguardando o mestre encerrar a rodada.
        </p>
      )}
      {!isMaster && status === 'idle' && (
        <p style={{ marginTop: 'var(--spacing-lab-4)', color: 'var(--color-lab-ink-3)' }}>
          Aguardando o mestre abrir o quiz.
        </p>
      )}
    </article>
  )
}

function Pill({ status }: { status: 'idle' | 'open' | 'closed' }) {
  const label = status === 'idle' ? 'aguardando' : status === 'open' ? 'ao vivo' : 'encerrado'
  const bg =
    status === 'open'
      ? 'var(--color-lab-mint)'
      : status === 'closed'
        ? 'var(--color-lab-rule)'
        : 'var(--color-lab-bg-2)'
  const color = status === 'open' ? '#fff' : 'var(--color-lab-ink)'
  return (
    <span
      style={{
        fontFamily: 'var(--font-lab-mono)',
        fontSize: 'var(--text-lab-xs)',
        fontWeight: 600,
        padding: '4px 10px',
        background: bg,
        color,
        borderRadius: 'var(--radius-lab-sm)',
        letterSpacing: 0.5,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </span>
  )
}

function optionBg(selected: boolean, correct: boolean, wrong: boolean): string {
  if (wrong) return 'color-mix(in srgb, var(--color-lab-rose) 12%, white)'
  if (correct) return 'color-mix(in srgb, var(--color-lab-mint) 12%, white)'
  if (selected) return 'var(--color-lab-bg-2)'
  return 'var(--color-lab-bg-2)'
}

function optionBorder(selected: boolean, correct: boolean, wrong: boolean): string {
  if (wrong) return 'var(--color-lab-rose)'
  if (correct) return 'var(--color-lab-mint)'
  if (selected) return 'var(--color-lab-accent)'
  return 'var(--color-lab-rule)'
}

function barColor(correct: boolean, wrong: boolean): string {
  if (wrong) return 'var(--color-lab-rose)'
  if (correct) return 'var(--color-lab-mint)'
  return 'var(--color-lab-accent)'
}

const btnPrimary: React.CSSProperties = {
  minHeight: 44,
  padding: '10px 20px',
  background: 'var(--color-lab-accent)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-lab-sm)',
  fontFamily: 'var(--font-lab-mono)',
  fontSize: 'var(--text-lab-sm)',
  fontWeight: 700,
  cursor: 'pointer',
}

const btnSecondary: React.CSSProperties = {
  ...btnPrimary,
  background: '#FFFFFF',
  color: '#0F1115',
  border: '1px solid #D8D5CB',
}
